import { useEffect } from 'react';

import type { AuthState } from '../auth/useAuth';
import { enablePush, getPushRegistration } from './pushTokens';

/**
 * 로그인 세션이 생기면 이 기기의 FCM 토큰을 조용히 등록한다 (PLAN 5-8-8장).
 *
 * 의도적 제약:
 * - **Notification 권한이 이미 granted일 때만** 등록한다. 여기서 requestPermission을
 *   호출하면 앱 첫 화면에 권한 팝업이 뜨는데, 가입 직전 사용자에게 알림 팝업은
 *   신뢰를 깎는다 (요구사항 4). 최초 허용은 설정 → 알림의 토글이 담당한다.
 * - 등록 실패는 조용히 로그만 남긴다 — 푸시는 부가 기능이고, 실패가 앱 사용을
 *   막아서는 안 된다. 상태 진단은 알림 설정 화면이 observePushRegistration으로 한다.
 * - privacy.notificationsOff는 서버 측 스위치다. 이 훅은 토큰 등록만 하고
 *   발송 여부 판정은 Functions가 한다 (PLAN 5-8-2장 수신자 필터).
 */
export function usePushRegistrationEffect(auth: AuthState): void {
  const uid = auth.firebaseUser?.uid ?? null;
  const authenticated = auth.status === 'authenticated';

  useEffect(() => {
    if (!authenticated || !uid) return;

    let cancelled = false;

    const ensureRegistered = async (forceRefresh = false) => {
      const current = getPushRegistration();
      // denied/unsupported 환경에서는 매번 재시도하지 않는다
      if (
        current.state === 'denied' ||
        current.state === 'unsupported' ||
        current.state === 'no-vapid-key' ||
        (current.state === 'active' && !forceRefresh)
      ) {
        return;
      }
      if (typeof Notification !== 'undefined' && Notification.permission !== 'granted') {
        // 권한이 없으면 프롬프트를 띄우지 않고 설정 화면의 토글에 맡긴다
        return;
      }

      const result = await enablePush(uid);
      if (cancelled) return;
      if (result.state === 'error') {
        console.warn('[talk2nIcK] 푸시 자동 등록 실패 — 알림 설정에서 다시 시도할 수 있습니다');
      }
    };

    void ensureRegistered();

    // 백그라운드에서 돌아올 때 서버에서 만료/정리된 토큰을 즉시 자가 치유(Self-Healing)하도록 갱신
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void ensureRegistered(true);
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [authenticated, uid]);
}
